import React, { useState } from 'react'
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { FORMAT_ELEMENT_COMMAND, FORMAT_TEXT_COMMAND, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND, REDO_COMMAND, UNDO_COMMAND } from "lexical";
import { FaAlignCenter, FaAlignJustify, FaAlignLeft, FaAlignRight, FaAngleLeft, FaAngleRight, FaBold, FaImage, FaIndent, FaItalic, FaLink, FaListOl, FaListUl, FaOutdent, FaUnderline } from "react-icons/fa";
import _ from 'lodash';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { useToolbarFormatState } from './useToolbarFormatState';
import ImageInsertModal from './ImageInsertModal';
import LinkInsertModal from './LinkInsertModal';
import './Toolbar.scss';

const BLOCK_TYPE_OPTIONS: { value: string, label: string }[] = [
  { value: 'paragraph', label: 'Akapit' },
  { value: 'h1', label: 'Nagłówek 1' },
  { value: 'h2', label: 'Nagłówek 2' },
  { value: 'h3', label: 'Nagłówek 3' },
  { value: 'h4', label: 'Nagłówek 4' },
  { value: 'h5', label: 'Nagłówek 5' },
  { value: 'h6', label: 'Nagłówek 6' },
  { value: 'quote', label: 'Cytat' },
];

const FONT_SIZE_OPTIONS: string[] = [
  '12px', '14px', '16px',
  '18px', '20px', '24px',
  '28px', '32px', '36px'
];

type ToolbarButtonItem = {
  type: 'button',
  onClick: () => void,
  className?: string,
  child: JSX.Element
}
type ToolbarSelectItem = {
  type: 'select',
  value: string,
  onChange: (value: string) => void,
  options: { value: string, label: string }[]
}
type ToolbarItem = ToolbarButtonItem | ToolbarSelectItem;

const Toolbar = () => {
  const [editor] = useLexicalComposerContext();
  const { activeFormats, blockType, fontSize, applyBlockType, applyFontSize } = useToolbarFormatState();

  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [showLinkModal, setShowLinkModal] = useState<boolean>(false);

  const groups: ToolbarItem[][] = [
    [{
      type: 'button',
      child: <FaAngleLeft />,
      onClick: () => editor.dispatchCommand(UNDO_COMMAND, undefined),
    },
    {
      type: 'button',
      child: <FaAngleRight />,
      onClick: () => editor.dispatchCommand(REDO_COMMAND, undefined),
    }],
    [{
      type: 'button',
      child: <FaBold />,
      className: activeFormats.has('bold') ? 'active' : '',
      onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'),
    },
    {
      type: 'button',
      child: <FaItalic />,
      className: activeFormats.has('italic') ? 'active' : '',
      onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'),
    },
    {
      type: 'button',
      child: <FaUnderline />,
      className: activeFormats.has('underline') ? 'active' : '',
      onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
    }],
    [{
      type: 'select',
      value: blockType,
      onChange: applyBlockType,
      options: BLOCK_TYPE_OPTIONS
    },
    {
      type: 'select',
      value: fontSize,
      onChange: applyFontSize,
      options: [
        { value: '', label: 'Domyślny rozmiar' },
        ..._.map(FONT_SIZE_OPTIONS, (size) => ({ value: size, label: size }))
      ]
    }],
    [{
      type: 'button',
      child: <FaListUl />,
      onClick: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    },
    {
      type: 'button',
      child: <FaListOl />,
      onClick: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    },
    {
      type: 'button',
      child: <FaOutdent />,
      onClick: () => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
    },
    {
      type: 'button',
      child: <FaIndent />,
      onClick: () => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
    }],
    [{
      type: 'button',
      child: <FaAlignLeft />,
      onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')
    },
    {
      type: 'button',
      child: <FaAlignCenter />,
      onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')
    },
    {
      type: 'button',
      child: <FaAlignRight />,
      onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')
    },
    {
      type: 'button',
      child: <FaAlignJustify />,
      onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')
    }],
    [{
      type: 'button',
      child: <FaLink />,
      onClick: () => setShowLinkModal(true),
    },
    {
      type: 'button',
      child: <FaImage />,
      onClick: () => setShowImageModal(true),
    }],
  ];

  const renderItem = (item: ToolbarItem, key: number): JSX.Element => {
    if (item.type === 'select') {
      return (
        <select
          key={key}
          className='Toolbar__select'
          value={item.value}
          onChange={(e) => item.onChange(e.target.value)}>
          {_.map(item.options,
            (option) => <option key={option.value} value={option.value}>
              {option.label}
            </option>)}
        </select>
      );
    }
    return (
      <button key={key} className={item.className} type='button' onClick={item.onClick}>
        {item.child}
      </button>
    );
  }

  return <div className='Toolbar'>
    {_.map(groups, (group: ToolbarItem[], groupIndex: number) => (
      <React.Fragment key={groupIndex}>
        {groupIndex > 0 && <span className='Toolbar__separator'>|</span>}
        {_.map(group, (item, itemIndex) => renderItem(item, itemIndex))}
      </React.Fragment>
    ))}
    <ImageInsertModal
      show={showImageModal}
      onClose={() => setShowImageModal(false)}
    />
    <LinkInsertModal
      show={showLinkModal}
      onClose={() => setShowLinkModal(false)}
    />
  </div>;
}

export default Toolbar
